import { useMemo, useSyncExternalStore } from "react";
import { Link } from "react-router-dom";
import { Art, PosterCard, Row } from "../components/Cards";
import * as I from "../components/Icons";
import { Page } from "../components/Layout";
import { allEpisodes, languageName, runtimeLabel } from "../lib/catalog";
import { inProgress, lastWatched, subscribe } from "../services/progress";
import { useCatalog } from "../state/CatalogContext";
import type { Title } from "../types";

function useProgressVersion() {
  return useSyncExternalStore(subscribe, () => JSON.stringify(inProgress().map((e) => [e.titleId, e.at])));
}

export function Hero({ t }: { t: Title }) {
  const m = t.media ?? allEpisodes(t)[0]?.media;
  const last = lastWatched(t.id);
  const playTo = last ? `/ver/${t.id}${last.episodeId ? `/${last.episodeId}` : ""}` : `/ver/${t.id}`;
  const audio = [...new Set(m?.audio.map((a) => languageName(a.language)) ?? [])];
  return (
    <section className="relative isolate min-h-[78vh] overflow-hidden" aria-label="Destacado">
      <Art k={t.backdrop ?? t.poster} alt="" eager className="absolute inset-0 -z-10 h-full w-full" />
      <div className="beam absolute inset-0 -z-10" />
      <div className="mx-auto flex min-h-[78vh] max-w-[1800px] flex-col justify-end px-4 pb-14 pt-28 sm:px-8 lg:px-12">
        <div className="hero-in max-w-3xl">
          <h1 className="hero-title">{t.title}</h1>
          <p className="mt-5 flex flex-wrap gap-x-4 gap-y-1 text-[15px] text-text/85">
            {t.year && <span>{t.year}</span>}
            {t.kind === "movie" && m && <span>{runtimeLabel(m.durationSec)}</span>}
            {t.kind === "series" && <span>{t.seasons?.length} temporada{t.seasons?.length === 1 ? "" : "s"}</span>}
            {t.genres?.slice(0, 3).map((g) => <span key={g} className="text-muted">{g}</span>)}
          </p>
          {t.overview && <p className="mt-4 max-w-[60ch] text-[17px] leading-relaxed text-text/80 line-clamp-3">{t.overview}</p>}
          {audio.length > 1 && <p className="mt-3 text-sm text-muted">Audio en {audio.join(", ")}</p>}
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to={playTo} className="btn btn-primary px-6 py-3 text-[17px]"><I.Play width={20} height={20} />{last ? "Continuar" : "Reproducir"}</Link>
            <Link to={`/titulo/${t.id}`} className="btn px-6 py-3 text-[17px]"><I.Info width={20} height={20} />Más información</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HomePage() {
  const { catalog } = useCatalog();
  useProgressVersion();
  const c = catalog!;
  const featured = useMemo(() => {
    const f = c.items.filter((t) => t.featured);
    const pool = f.length ? f : [...c.items].sort((a, b) => b.addedAt.localeCompare(a.addedAt)).slice(0, 5);
    return pool[new Date().getDate() % Math.max(1, pool.length)];
  }, [c]);
  if (!c.items.length) {
    return (
      <Page title="Inicio">
        <div className="mx-auto max-w-xl py-24 text-center">
          <h1 className="font-display text-4xl">Tu biblioteca está vacía</h1>
          <p className="mt-4 text-muted">Importa un vídeo en Radiant Media Manager y pulsa «Procesar y subir». Aparecerá aquí automáticamente, sin volver a desplegar la web.</p>
          {c.channels.length > 0 && <Link to="/canales" className="btn mt-6">Ver canales</Link>}
        </div>
      </Page>
    );
  }
  const continuing = inProgress().map((e) => ({ e, t: c.items.find((x) => x.id === e.titleId) })).filter((x) => x.t) as { e: ReturnType<typeof inProgress>[number]; t: Title }[];
  const recent = [...c.items].sort((a, b) => b.addedAt.localeCompare(a.addedAt)).slice(0, 20);
  const by = (cat: Title["category"]) => c.items.filter((t) => t.category === cat);
  const genres = [...new Map(c.items.flatMap((t) => t.genres ?? []).map((g) => [g, 0])).keys()]
    .map((g) => ({ g, items: c.items.filter((t) => t.genres?.includes(g)) }))
    .filter((x) => x.items.length >= 3)
    .sort((a, b) => b.items.length - a.items.length)
    .slice(0, 4);

  return (
    <>
      {featured && <Hero t={featured} />}
      <div className="mx-auto max-w-[1800px] px-4 pb-16 sm:px-8 lg:px-12">
        {continuing.length > 0 && (
          <Row title="Continuar viendo">
            {continuing.map(({ e, t }) => <PosterCard key={t.id} t={t} progress={e.dur ? e.pos / e.dur : 0} />)}
          </Row>
        )}
        <Row title="Añadidos recientemente">{recent.map((t) => <PosterCard key={t.id} t={t} />)}</Row>
        {by("movie").length > 0 && <Row title="Películas" link={{ to: "/peliculas", label: "Ver todas" }}>{by("movie").map((t) => <PosterCard key={t.id} t={t} />)}</Row>}
        {by("series").length > 0 && <Row title="Series" link={{ to: "/series", label: "Ver todas" }}>{by("series").map((t) => <PosterCard key={t.id} t={t} />)}</Row>}
        {by("anime").length > 0 && <Row title="Anime" link={{ to: "/anime", label: "Ver todo" }}>{by("anime").map((t) => <PosterCard key={t.id} t={t} />)}</Row>}
        {c.collections.map((col) => (
          <Row key={col.id} title={col.name}>
            {col.itemIds.map((id) => c.items.find((t) => t.id === id)).filter(Boolean).map((t) => <PosterCard key={t!.id} t={t!} />)}
          </Row>
        ))}
        {genres.map(({ g, items }) => <Row key={g} title={g}>{items.map((t) => <PosterCard key={t.id} t={t} />)}</Row>)}
      </div>
    </>
  );
}
